import React, { useState, useEffect, useCallback } from 'react';
import { tokens } from "../../theme";
import {
    Box, Typography, useTheme, Button, IconButton, InputBase,
    Breadcrumbs, Link, Card, CardContent, CardActionArea, Chip,
    Menu, MenuItem, CircularProgress, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField,
} from "@mui/material";
import Header from "../../components/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import LaunchIcon from '@mui/icons-material/Launch';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QuizIcon from '@mui/icons-material/Quiz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SortIcon from '@mui/icons-material/Sort';
import { ToggleButton, ToggleButtonGroup, Table, TableHead, TableBody, TableRow, TableCell, Divider, Autocomplete, FormControlLabel, Checkbox } from '@mui/material';
import api from '../../utils/v2Api';
import dayjs from 'dayjs';
import axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const TestLibrary = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Preferences — consolidated in one localStorage key
    const getPrefs = () => {
        try { return JSON.parse(localStorage.getItem('testLibraryPrefs')) || {}; } catch { return {}; }
    };
    const savePrefs = (updates) => {
        const current = getPrefs();
        localStorage.setItem('testLibraryPrefs', JSON.stringify({ ...current, ...updates }));
    };
    const prefs = getPrefs();
    const [viewMode, setViewMode] = useState(prefs.viewMode || 'grid');
    const [sortBy, setSortBy] = useState(prefs.sortBy || 'name-asc');
    const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
    const handleViewModeChange = (_, newMode) => {
        if (newMode) {
            setViewMode(newMode);
            savePrefs({ viewMode: newMode });
            setSearch('');
            setSearchParams({});
            if (newMode === 'column') {
                columnManualNav.current = false;
                initColumnView();
            }
        }
    };

    // Column view state — each column has its own data
    // columns[0] = root folders, columns[1] = children of selected root, etc.
    const [columns, setColumns] = useState([]); // [{ id, name, folders: [], tests: [] }, ...]
    const [columnSelected, setColumnSelected] = useState([]); // [selectedIdInCol0, selectedIdInCol1, ...]

    const [loading, setLoading] = useState(true);
    const [folders, setFolders] = useState([]);
    const [tests, setTests] = useState([]);
    const [testsTotal, setTestsTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');

    // Path-based navigation: ?path=id1,id2,id3 — each is a folder id in the breadcrumb trail
    // The last id in the path is the current folder
    const pathStr = searchParams.get('path') || '';
    const pathIds = pathStr ? pathStr.split(',').map(Number) : [];
    const currentFolderId = pathIds.length > 0 ? pathIds[pathIds.length - 1] : null;

    // Breadcrumb labels — fetched alongside navigation
    const [breadcrumbs, setBreadcrumbs] = useState([]); // [{ id, name }, ...]

    // Dialogs
    const confirmRef = React.useRef();
    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderParentId, setNewFolderParentId] = useState(null);
    const [allFoldersFlat, setAllFoldersFlat] = useState([]);
    const [contextMenu, setContextMenu] = useState(null);
    const [renameFolderId, setRenameFolderId] = useState(null);
    const [renameFolderName, setRenameFolderName] = useState('');
    const [moveOpen, setMoveOpen] = useState(false);
    const [moveItemId, setMoveItemId] = useState(null);
    const [moveItemType, setMoveItemType] = useState(null); // 'test' or 'folder'
    const [moveTargetFolderId, setMoveTargetFolderId] = useState(null);

    // Go to Course dialog
    const [goToCourseOpen, setGoToCourseOpen] = useState(false);
    const [courseList, setCourseList] = useState([]);
    const [courseLoading, setCourseLoading] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [currentSemesterOnly, setCurrentSemesterOnly] = useState(true);
    const [currentSemesterId, setCurrentSemesterId] = useState(null);

    // Quick Assign
    const [quickAssignOpen, setQuickAssignOpen] = useState(false);
    const [quickAssignTestId, setQuickAssignTestId] = useState(null);
    const [quickAssignTestTitle, setQuickAssignTestTitle] = useState('');
    const [quickAssignTitle, setQuickAssignTitle] = useState('');
    const [quickAssignInstructions, setQuickAssignInstructions] = useState('');
    const [quickAssignCourse, setQuickAssignCourse] = useState(null);
    const [quickAssignDueDate, setQuickAssignDueDate] = useState('');
    const [quickAssignLoading, setQuickAssignLoading] = useState(false);
    const [quickAssignError, setQuickAssignError] = useState('');
    const [quickAssignAllStudents, setQuickAssignAllStudents] = useState([]);
    const [quickAssignSelectedStudents, setQuickAssignSelectedStudents] = useState([]);
    const [quickAssignStudentsLoading, setQuickAssignStudentsLoading] = useState(false);

    // Track whether column view was manually navigated (to avoid search reset overwriting it)
    const columnManualNav = React.useRef(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
            if (viewMode === 'column') {
                if (search.trim()) {
                    columnManualNav.current = false;
                    fetchColumnSearch(search.trim());
                } else if (!columnManualNav.current) {
                    initColumnView();
                }
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchColumnSearch = async (query) => {
        try {
            const [foldersRes, testsRes] = await Promise.all([
                api.get('/test-categories', { search: query }),
                api.get('/tests', { search: query }),
            ]);
            setColumns([{
                id: null,
                name: `Search: "${query}"`,
                folders: foldersRes.data.data || [],
                tests: testsRes.data.data || [],
            }]);
            setColumnSelected([]);
        } catch (err) {
            console.error('Failed to search in column view:', err);
        }
    };

    // Fetch folders (children of current folder)
    const fetchFolders = useCallback(async () => {
        try {
            const params = searchDebounced
                ? { search: searchDebounced }
                : { parent_id: currentFolderId || '' };
            const res = await api.get('/test-categories', params);
            setFolders(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    }, [currentFolderId, searchDebounced]);

    // Fetch tests for current folder
    const fetchTests = useCallback(async () => {
        try {
            const params = {};
            if (currentFolderId) params.category_id = currentFolderId;
            else if (!searchDebounced) params.category_id = 'root';
            if (searchDebounced) params.search = searchDebounced;
            const res = await api.get('/tests', params);
            setTests(res.data.data || []);
            setTestsTotal(res.data.total || 0);
        } catch (err) {
            console.error('Failed to fetch tests:', err);
        }
    }, [currentFolderId, searchDebounced]);

    // Fetch breadcrumb names when path changes
    useEffect(() => {
        const fetchBreadcrumbs = async () => {
            if (pathIds.length === 0) {
                setBreadcrumbs([]);
                return;
            }
            try {
                const crumbs = [];
                for (const id of pathIds) {
                    const res = await api.get(`/test-categories/${id}`);
                    crumbs.push({ id, name: res.data.data.name });
                }
                setBreadcrumbs(crumbs);
            } catch (err) {
                console.error('Failed to fetch breadcrumbs:', err);
            }
        };
        fetchBreadcrumbs();
    }, [pathStr]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch column data for column view
    const initColumnView = useCallback(async () => {
        try {
            const res = await api.get('/test-categories', { parent_id: '' });
            setColumns([{ id: null, name: 'Root', folders: res.data.data || [], tests: [] }]);
            setColumnSelected([]);
        } catch (err) {
            console.error('Failed to init column view:', err);
        }
    }, []);

    const handleColumnSelect = async (colIndex, folderId) => {
        // Update selection — truncate anything after this column
        const newSelected = [...columnSelected.slice(0, colIndex), folderId];
        setColumnSelected(newSelected);

        // Fetch children of selected folder
        try {
            const [foldersRes, testsRes] = await Promise.all([
                api.get('/test-categories', { parent_id: folderId }),
                api.get('/tests', { category_id: folderId }),
            ]);
            const folder = columns[colIndex].folders.find(f => f.id === folderId);
            const newCol = {
                id: folderId,
                name: folder?.name || '',
                folders: foldersRes.data.data || [],
                tests: testsRes.data.data || [],
            };
            // Truncate columns after current + add new
            setColumns(prev => [...prev.slice(0, colIndex + 1), newCol]);
        } catch (err) {
            console.error('Failed to fetch column children:', err);
        }
    };

    // Reveal a folder's full path in column view (for search result clicks)
    const revealFolderInColumns = async (folderId) => {
        try {
            // Get ancestor chain: [root, ..., parent, self]
            const ancestorRes = await api.get(`/test-categories/${folderId}/ancestors`);
            const chain = ancestorRes.data.data || [];
            if (chain.length === 0) return;

            // Fetch root column
            const rootRes = await api.get('/test-categories', { parent_id: '' });
            const newColumns = [{ id: null, name: 'Root', folders: rootRes.data.data || [], tests: [] }];
            const newSelected = [];

            // For each ancestor, fetch its siblings (parent's children) and mark it selected
            for (let i = 0; i < chain.length; i++) {
                const nodeId = chain[i].id;
                newSelected.push(nodeId);

                const [foldersRes, testsRes] = await Promise.all([
                    api.get('/test-categories', { parent_id: nodeId }),
                    api.get('/tests', { category_id: nodeId }),
                ]);

                newColumns.push({
                    id: nodeId,
                    name: chain[i].name,
                    folders: foldersRes.data.data || [],
                    tests: testsRes.data.data || [],
                });
            }

            setColumns(newColumns);
            setColumnSelected(newSelected);
            columnManualNav.current = true;
            setSearch('');
        } catch (err) {
            console.error('Failed to reveal folder in columns:', err);
        }
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([fetchFolders(), fetchTests()]);
            if (viewMode === 'column' && columns.length === 0) await initColumnView();
            setLoading(false);
        };
        load();
    }, [fetchFolders, fetchTests, viewMode, initColumnView]); // eslint-disable-line react-hooks/exhaustive-deps

    // Navigation
    const openFolder = async (id) => {
        if (searchDebounced) {
            // Search result click — fetch ancestor chain and navigate to full path
            try {
                const res = await api.get(`/test-categories/${id}/ancestors`);
                const chain = res.data.data || [];
                const fullPath = chain.map(c => c.id).join(',');
                setSearch('');
                setSearchParams({ path: fullPath });
            } catch (err) {
                // Fallback: just navigate directly
                setSearchParams({ path: String(id) });
            }
        } else {
            const newPath = [...pathIds, id].join(',');
            setSearchParams({ path: newPath });
        }
    };

    const navigateToBreadcrumb = (index) => {
        if (index < 0) {
            // Root
            setSearchParams({});
        } else {
            const newPath = pathIds.slice(0, index + 1).join(',');
            setSearchParams({ path: newPath });
        }
    };

    // Open create folder dialog — fetch all folders for the picker, default to current location
    const openCreateFolderDialog = async () => {
        let flat = [];
        try {
            const res = await api.get('/test-categories', { tree: true });
            flat = flattenFolders(res.data.data || []);
        } catch (err) {
            flat = [];
        }
        // Prepend a "Root" option so there's always something selected
        const withRoot = [{ id: null, name: 'Test Library', label: 'Test Library', depth: 0, path: 'Test Library' }, ...flat];
        setAllFoldersFlat(withRoot);

        // Default parent to current folder location
        let defaultParent = null;
        if (viewMode === 'column' && columnSelected.length > 0) {
            defaultParent = columnSelected[columnSelected.length - 1];
        } else if (currentFolderId) {
            defaultParent = currentFolderId;
        }
        setNewFolderParentId(defaultParent);
        setNewFolderName('');
        setCreateFolderOpen(true);
    };

    function flattenFolders(items, depth = 0, parentPath = '') {
        const result = [];
        for (const item of items) {
            const indent = depth > 0 ? '  '.repeat(depth - 1) + '└ ' : '';
            // Full ancestry path so same-named folders in different places are
            // distinguishable in the pickers (e.g. two folders both named "Vocab").
            const path = parentPath ? `${parentPath} > ${item.name}` : item.name;
            result.push({ id: item.id, name: item.name, label: indent + item.name, depth, path });
            if (item.children?.length > 0) {
                result.push(...flattenFolders(item.children, depth + 1, path));
            }
        }
        return result;
    }

    /** All ids in the subtree rooted at targetId (including targetId itself). */
    function collectSubtreeIds(items, targetId, inTarget = false, acc = new Set()) {
        for (const item of items) {
            const hit = inTarget || item.id === targetId;
            if (hit) acc.add(item.id);
            if (item.children?.length > 0) collectSubtreeIds(item.children, targetId, hit, acc);
        }
        return acc;
    }

    const [createFolderError, setCreateFolderError] = useState('');

    // Create folder
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            setCreateFolderError('Folder name is required');
            return;
        }
        setCreateFolderError('');
        try {
            await api.post('/test-categories', {
                name: newFolderName.trim(),
                label: newFolderName.trim(),
                parent_id: newFolderParentId || null,
            });
            setNewFolderName('');
            setNewFolderParentId(null);
            setCreateFolderOpen(false);
            fetchFolders();
            if (viewMode === 'column') initColumnView();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create folder');
        }
    };

    // Delete category
    const handleDeleteCategory = (id) => {
        confirmRef.current?.open({
            title: 'Delete Folder',
            message: 'Are you sure you want to delete this folder? This cannot be undone.',
            confirmLabel: 'Delete',
            onConfirm: async () => {
                try {
                    await api.delete(`/test-categories/${id}`);
                    fetchFolders();
                    if (id === currentFolderId) navigateToBreadcrumb(pathIds.length - 2);
                } catch (err) {
                    alert(err.response?.data?.message || 'Failed to delete folder');
                }
            },
        });
    };

    // Rename folder
    const handleRenameFolder = async () => {
        if (!renameFolderName.trim() || !renameFolderId) return;
        try {
            await api.put(`/test-categories/${renameFolderId}`, { name: renameFolderName.trim(), label: renameFolderName.trim() });
            setRenameFolderId(null);
            setRenameFolderName('');
            fetchFolders();
            if (viewMode === 'column') initColumnView();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to rename folder');
        }
    };

    // Move test to folder
    const openMoveDialog = async (itemId, type) => {
        let flat = [];
        let excluded = new Set();
        try {
            const res = await api.get('/test-categories', { tree: true });
            const tree = res.data.data || [];
            flat = flattenFolders(tree);
            // Moving a folder into itself or any of its descendants would create
            // a parent cycle (the server rejects it too) — exclude the whole subtree.
            if (type === 'folder') excluded = collectSubtreeIds(tree, itemId);
        } catch { flat = []; }
        const filtered = type === 'folder'
            ? flat.filter(f => !excluded.has(f.id))
            : flat;
        const withRoot = [{ id: null, name: 'Test Library (Root)', label: 'Test Library (Root)', depth: 0, path: 'Test Library (Root)' }, ...filtered];
        setAllFoldersFlat(withRoot);
        setMoveItemId(itemId);
        setMoveItemType(type);
        setMoveTargetFolderId(currentFolderId);
        setMoveOpen(true);
    };

    const handleMove = async () => {
        if (!moveItemId) return;
        try {
            if (moveItemType === 'test') {
                await api.put(`/tests/${moveItemId}`, { category_id: moveTargetFolderId || null });
            } else {
                await api.put(`/test-categories/${moveItemId}`, { parent_id: moveTargetFolderId || null });
            }
            setMoveOpen(false);
            setMoveItemId(null);
            fetchFolders();
            fetchTests();
            if (viewMode === 'column') initColumnView();
        } catch (err) {
            alert(err.response?.data?.message || `Failed to move ${moveItemType}`);
        }
    };

    // Go to Course
    const v1 = (path) => axios.get(`${process.env.REACT_APP_URL}/api/v1${path}`, { headers: { Authorization: getAuthHeader() } });

    const openGoToCourse = async () => {
        setGoToCourseOpen(true);
        setSelectedCourse(null);
        setCourseLoading(true);
        try {
            // Fetch current semester first
            let semId = currentSemesterId;
            if (!semId) {
                const semRes = await v1('/semesters/current-semester');
                const allSemRes = await v1('/semesters');
                const cur = semRes.data;
                const seasonMap = { Spring: 1, Summer: 2, Fall: 3, Winter: 4 };
                const match = (allSemRes.data || []).find(s => Number(s.year) === Number(cur.year) && Number(s.season) === seasonMap[cur.semester]);
                if (match) {
                    semId = match.id;
                    setCurrentSemesterId(semId);
                }
            }

            // Fetch courses
            const res = await v1('/courses/all/list');
            const all = res.data.all_courses || [];
            // Tag each course with its semester IDs for filtering
            const tagged = all.map(c => ({
                id: c.id,
                uuid: c.uuid,
                code: c.code,
                title: c.course_title,
                label: `${c.code} — ${c.course_title}`,
                semesterIds: (c.course_semesters || []).map(cs => cs.semester_id),
            }));
            setCourseList(tagged);
        } catch (err) {
            console.error('Failed to fetch courses:', err);
        } finally {
            setCourseLoading(false);
        }
    };

    const filteredCourses = currentSemesterOnly && currentSemesterId
        ? courseList.filter(c => c.semesterIds.includes(currentSemesterId))
        : courseList;

    // Quick Assign
    const openQuickAssign = async (testId, testTitle) => {
        setQuickAssignTestId(testId);
        setQuickAssignTestTitle(testTitle);
        setQuickAssignTitle(testTitle);
        setQuickAssignInstructions('');
        setQuickAssignCourse(null);
        setQuickAssignDueDate('');
        setQuickAssignError('');
        setQuickAssignAllStudents([]);
        setQuickAssignSelectedStudents([]);
        setQuickAssignOpen(true);
        // Reuse course fetch from Go to Course
        if (courseList.length === 0) {
            setCourseLoading(true);
            try {
                let semId = currentSemesterId;
                if (!semId) {
                    const semRes = await v1('/semesters/current-semester');
                    const allSemRes = await v1('/semesters');
                    const cur = semRes.data;
                    const seasonMap = { Spring: 1, Summer: 2, Fall: 3, Winter: 4 };
                    const match = (allSemRes.data || []).find(s => Number(s.year) === Number(cur.year) && Number(s.season) === seasonMap[cur.semester]);
                    if (match) { semId = match.id; setCurrentSemesterId(semId); }
                }
                const res = await v1('/courses/all/list');
                const all = res.data.all_courses || [];
                setCourseList(all.map(c => ({
                    id: c.id, uuid: c.uuid, code: c.code, title: c.course_title,
                    label: `${c.code} — ${c.course_title}`,
                    semesterIds: (c.course_semesters || []).map(cs => cs.semester_id),
                })));
            } catch (err) { console.error('Failed to fetch courses:', err); }
            finally { setCourseLoading(false); }
        }
    };

    const handleQuickAssign = async () => {
        if (!quickAssignCourse || !quickAssignDueDate) {
            setQuickAssignError('Select a course and set a due date');
            return;
        }
        setQuickAssignLoading(true);
        setQuickAssignError('');
        try {
            // If all students are still selected, send empty array (= assign to all)
            const removedSome = quickAssignSelectedStudents.length < quickAssignAllStudents.length;
            const studentIds = removedSome ? quickAssignSelectedStudents.map(s => s.id) : [];
            await api.post(`/courses/${quickAssignCourse.id}/tests/${quickAssignTestId}`, {
                title: quickAssignTitle,
                content: quickAssignInstructions || null,
                due_date: quickAssignDueDate,
                posted_by: localStorage.getItem('userName'),
                show_on_homework: true,
                student_ids: studentIds,
            });
            setQuickAssignOpen(false);
        } catch (err) {
            setQuickAssignError(err.response?.data?.message || 'Failed to assign');
        } finally {
            setQuickAssignLoading(false);
        }
    };

    // Delete test
    const handleDeleteTest = (id) => {
        confirmRef.current?.open({
            title: 'Delete Test',
            message: 'Are you sure you want to delete this test?',
            confirmLabel: 'Delete',
            onConfirm: async () => {
                try {
                    await api.delete(`/tests/${id}`);
                    fetchTests();
                } catch (err) {
                    alert(err.response?.data?.message || 'Failed to delete test');
                }
            },
        });
    };

    // ─── Render ────────────────────────────────────────────────

    const renderBreadcrumbs = () => (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
            <Link
                component="button"
                underline="hover"
                onClick={() => navigateToBreadcrumb(-1)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: breadcrumbs.length > 0 ? colors.grey[200] : colors.blueAccent[500], fontWeight: breadcrumbs.length > 0 ? 400 : 600 }}
            >
                <HomeIcon fontSize="small" /> Test Library
            </Link>
            {breadcrumbs.map((crumb, idx) => {
                const isLast = idx === breadcrumbs.length - 1;
                return isLast ? (
                    <Typography key={crumb.id} color={colors.blueAccent[500]} fontWeight={600}>
                        {crumb.name}
                    </Typography>
                ) : (
                    <Link
                        key={crumb.id}
                        component="button"
                        underline="hover"
                        onClick={() => navigateToBreadcrumb(idx)}
                        sx={{ color: colors.grey[200] }}
                    >
                        {crumb.name}
                    </Link>
                );
            })}
        </Breadcrumbs>
    );

    // Sort items
    const sortItems = (items) => {
        if (sortBy === 'manual') return items; // keep server order
        return [...items].sort((a, b) => {
            switch (sortBy) {
                case 'name-asc': return (a.name || a.title || '').localeCompare(b.name || b.title || '');
                case 'name-desc': return (b.name || b.title || '').localeCompare(a.name || a.title || '');
                case 'date-asc': return new Date(a.createdAt) - new Date(b.createdAt);
                case 'date-desc': return new Date(b.createdAt) - new Date(a.createdAt);
                default: return 0;
            }
        });
    };

    const sortedFolders = sortItems(folders);
    const sortedTests = sortItems(tests);

    const handleSortChange = (newSort) => {
        setSortBy(newSort);
        savePrefs({ sortBy: newSort });
        setSortMenuAnchor(null);
    };

    // Drag and drop reorder (only active in manual sort mode)
    const isDragEnabled = sortBy === 'manual';

    const handleDragEnd = async (result) => {
        if (!result.destination || !isDragEnabled) return;
        const { source, destination, draggableId } = result;
        if (source.index === destination.index) return;

        const isFolder = draggableId.startsWith('folder-');
        const items = isFolder ? [...sortedFolders] : [...sortedTests];
        const [moved] = items.splice(source.index, 1);
        items.splice(destination.index, 0, moved);

        // Optimistic update
        if (isFolder) {
            const reordered = items.map((item, i) => ({ ...item, order: i }));
            setFolders(reordered);
            // Persist
            try {
                await api.put('/test-categories/order', { items: reordered.map((f, i) => ({ id: f.id, order: i })) });
            } catch (err) {
                console.error('Failed to save folder order:', err);
                fetchFolders();
            }
        } else {
            const reordered = items.map((item, i) => ({ ...item, order: i }));
            setTests(reordered);
            try {
                await api.put('/tests/order', { items: reordered.map((t, i) => ({ id: t.id, order: i })) });
            } catch (err) {
                console.error('Failed to save test order:', err);
                fetchTests();
            }
        }
    };

    const SORT_OPTIONS = [
        { value: 'name-asc', label: 'Name A → Z' },
        { value: 'name-desc', label: 'Name Z → A' },
        { value: 'date-asc', label: 'Date Created (oldest)' },
        { value: 'date-desc', label: 'Date Created (newest)' },
        { value: 'manual', label: 'Manual Order' },
    ];

    const renderEmptyState = () => (
        <Box textAlign="center" mt={6}>
            <QuizIcon sx={{ fontSize: 64, color: colors.grey[500], mb: 2 }} />
            <Typography variant="h5" color={colors.grey[300]}>
                {searchDebounced ? 'No results found' : !currentFolderId ? 'No folders yet' : 'This folder is empty'}
            </Typography>
            <Typography variant="body2" color={colors.grey[500]} mt={1}>
                {!currentFolderId ? 'Create a folder to organize your tests' : 'Add a test or subfolder'}
            </Typography>
        </Box>
    );

    const renderFolderCard = (category) => (
        <Card
            key={category.id}
            onClick={() => openFolder(category.id)}
            sx={{
                bgcolor: colors.primary[600],
                '&:hover': { bgcolor: colors.primary[700] },
                transition: 'background-color 0.2s',
                cursor: 'pointer',
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <FolderIcon sx={{ fontSize: 40, color: colors.blueAccent[400] }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600}>{category.name}</Typography>
                    {category.children?.length > 0 && (
                        <Typography variant="body2" color={colors.grey[300]}>
                            {category.children.length} subfolder{category.children.length !== 1 ? 's' : ''}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setContextMenu({ anchorEl: e.currentTarget, item: category, type: 'category' });
                    }}
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>
        </Card>
    );

    const renderTestCard = (test) => (
        <Card
            key={test.id}
            onClick={() => navigate(`/admin/tests-v2/${test.id}/edit`)}
            sx={{
                bgcolor: colors.primary[600],
                '&:hover': { bgcolor: colors.primary[700] },
                transition: 'background-color 0.2s',
                cursor: 'pointer',
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <DescriptionIcon sx={{ fontSize: 36, color: colors.greenAccent[400] }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>{test.title}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                <Chip label={new Date(test.createdAt).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })} size="small" variant="outlined" />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setContextMenu({ anchorEl: e.currentTarget, item: test, type: 'test' });
                        }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Box>
            </Box>
        </Card>
    );

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header title="Test Library" subtitle="Manage test categories and tests" />
                <Box display="flex" gap={1}>
                    {process.env.NODE_ENV === 'development' && <>
                        <Button
                            variant="outlined"
                            startIcon={<QuizIcon />}
                            onClick={() => navigate('/admin/question-bank')}
                        >
                            Question Bank
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => navigate('/admin/tests-v2/generate')}
                        >
                            Generate Test
                        </Button>
                    </>}
                    <Button
                        variant="outlined"
                        startIcon={<LaunchIcon />}
                        onClick={openGoToCourse}
                    >
                        Go to Course
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<CreateNewFolderIcon />}
                        onClick={openCreateFolderDialog}
                    >
                        New Folder
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            const folderId = viewMode === 'column' && columnSelected.length > 0
                                ? columnSelected[columnSelected.length - 1]
                                : currentFolderId;
                            navigate(folderId ? `/admin/tests-v2/add?folder=${folderId}` : '/admin/tests-v2/add');
                        }}
                        color="secondary"
                    >
                        Add Test
                    </Button>
                </Box>
            </Box>

            {/* Search Bar + View Toggle */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box
                    display="flex"
                    bgcolor={colors.primary[600]}
                    borderRadius="3px"
                    boxShadow={1}
                    sx={{ maxWidth: 400, flex: 1 }}
                >
                    <InputBase
                        sx={{ ml: 2, flex: 1 }}
                        placeholder="Search tests and folders..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <IconButton type="button" sx={{ p: 1 }}>
                        <SearchIcon />
                    </IconButton>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                    <Tooltip title="Sort">
                        <IconButton onClick={(e) => setSortMenuAnchor(e.currentTarget)} size="small">
                            <SortIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)}>
                        {SORT_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} onClick={() => handleSortChange(opt.value)} selected={sortBy === opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Menu>
                    <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
                        <ToggleButton value="grid"><Tooltip title="Grid"><GridViewIcon fontSize="small" /></Tooltip></ToggleButton>
                        <ToggleButton value="list"><Tooltip title="List"><ViewListIcon fontSize="small" /></Tooltip></ToggleButton>
                        <ToggleButton value="column"><Tooltip title="Column"><ViewColumnIcon fontSize="small" /></Tooltip></ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {viewMode !== 'column' && renderBreadcrumbs()}

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : viewMode === 'grid' ? (
                /* ─── Grid View ─── */
                <Box>
                    {isDragEnabled && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Drag-and-drop reordering works best in List or Column view.
                        </Typography>
                    )}
                    {sortedFolders.length > 0 && (
                        <Box mb={3}>
                            <Typography variant="overline" color={colors.grey[300]} mb={1} display="block">Folders</Typography>
                            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" gap={2}>
                                {sortedFolders.map(c => renderFolderCard(c))}
                            </Box>
                        </Box>
                    )}
                    {sortedTests.length > 0 && (
                        <Box>
                            <Typography variant="overline" color={colors.grey[300]} mb={1} display="block">
                                Tests {testsTotal > 0 && `(${testsTotal})`}
                            </Typography>
                            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))" gap={2}>
                                {sortedTests.map(renderTestCard)}
                            </Box>
                        </Box>
                    )}
                    {sortedFolders.length === 0 && sortedTests.length === 0 && renderEmptyState()}
                </Box>
            ) : viewMode === 'list' ? (
                /* ─── List View ─── */
                <DragDropContext onDragEnd={handleDragEnd}>
                <Box>
                    <Table size="small" sx={{ bgcolor: colors.primary[600], borderRadius: 1 }}>
                        <TableHead>
                            <TableRow>
                                {isDragEnabled && <TableCell sx={{ width: 30 }} />}
                                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Tags</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <Droppable droppableId="list-folders" isDropDisabled={!isDragEnabled}>
                        {(provided) => (
                        <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                            {sortedFolders.map((folder, i) => (
                                <Draggable key={`folder-${folder.id}`} draggableId={`folder-${folder.id}`} index={i} isDragDisabled={!isDragEnabled}>
                                {(prov, snap) => (
                                <TableRow
                                    ref={prov.innerRef}
                                    {...prov.draggableProps}
                                    key={`f-${folder.id}`}
                                    hover
                                    sx={{ cursor: 'pointer', opacity: snap.isDragging ? 0.7 : 1 }}
                                    onClick={() => openFolder(folder.id)}
                                >
                                    {isDragEnabled && <TableCell {...prov.dragHandleProps} sx={{ width: 30, cursor: 'grab' }}>⠿</TableCell>}
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FolderIcon sx={{ color: colors.blueAccent[400], fontSize: 20 }} />
                                            <Typography fontWeight={600}>{folder.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">Folder</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {folder.children?.length > 0 && (
                                            <Chip label={`${folder.children.length} sub`} size="small" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setContextMenu({ anchorEl: e.currentTarget, item: folder, type: 'category' }); }}>
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </TableBody>
                        )}
                        </Droppable>
                        <Droppable droppableId="list-tests" isDropDisabled={!isDragEnabled}>
                        {(provided) => (
                        <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                            {sortedTests.map((test, i) => (
                                <Draggable key={`test-${test.id}`} draggableId={`test-${test.id}`} index={i} isDragDisabled={!isDragEnabled}>
                                {(prov, snap) => (
                                <TableRow
                                    ref={prov.innerRef}
                                    {...prov.draggableProps}
                                    key={`t-${test.id}`}
                                    hover
                                    sx={{ cursor: 'pointer', opacity: snap.isDragging ? 0.7 : 1 }}
                                    onClick={() => navigate(`/admin/tests-v2/${test.id}/edit`)}
                                >
                                    {isDragEnabled && <TableCell {...prov.dragHandleProps} sx={{ width: 30, cursor: 'grab' }}>⠿</TableCell>}
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <DescriptionIcon sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
                                            <Typography>{test.title}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">Test</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5}>
                                                                                        <Chip label={new Date(test.createdAt).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })} size="small" variant="outlined" />
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setContextMenu({ anchorEl: e.currentTarget, item: test, type: 'test' }); }}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </TableBody>
                        )}
                        </Droppable>
                    </Table>
                    {sortedFolders.length === 0 && sortedTests.length === 0 && renderEmptyState()}
                </Box>
                </DragDropContext>
            ) : (
                /* ─── Column View (Finder-style) ─── */
                <DragDropContext onDragEnd={async (result) => {
                    if (!result.destination || !isDragEnabled) return;
                    const { source, destination, draggableId } = result;
                    if (source.index === destination.index && source.droppableId === destination.droppableId) return;

                    // Parse droppableId: "col-{colIdx}-folders" or "col-{colIdx}-tests"
                    const [, colIdxStr, type] = source.droppableId.split('-');
                    const colIdx = parseInt(colIdxStr);
                    const isFolder = type === 'folders';
                    const items = isFolder ? [...columns[colIdx].folders] : [...columns[colIdx].tests];
                    const [moved] = items.splice(source.index, 1);
                    items.splice(destination.index, 0, moved);

                    // Optimistic update
                    setColumns(prev => prev.map((c, i) => i === colIdx ? { ...c, [isFolder ? 'folders' : 'tests']: items } : c));

                    // Persist
                    try {
                        const endpoint = isFolder ? '/test-categories/order' : '/tests/order';
                        await api.put(endpoint, { items: items.map((item, i) => ({ id: item.id, order: i })) });
                    } catch (err) {
                        console.error('Failed to save column order:', err);
                    }
                }}>
                <Box display="flex" sx={{ border: `1px solid ${colors.grey[700]}`, borderRadius: 1, height: 'calc(100vh - 300px)', overflowX: 'auto' }}>
                    {columns.map((col, colIdx) => {
                        const selectedInCol = columnSelected[colIdx];
                        const isSearchResults = col.name.startsWith('Search:');
                        const colSx = { minWidth: 220, maxWidth: 260, borderRight: `1px solid ${colors.grey[700]}`, overflowY: 'auto', bgcolor: colors.primary[600], flexShrink: 0 };
                        const itemHoverSx = { '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' } };
                        const selectedBg = theme.palette.mode === 'dark' ? 'rgba(104,112,250,0.15)' : 'rgba(104,112,250,0.1)';

                        return (
                            <Box key={col.id ?? 'root'} sx={colSx}>
                                <Box sx={{ p: 1 }}>
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ px: 1 }}>{col.name}</Typography>
                                </Box>
                                <Droppable droppableId={`col-${colIdx}-folders`} isDropDisabled={!isDragEnabled}>
                                {(provided) => (
                                <Box ref={provided.innerRef} {...provided.droppableProps}>
                                {col.folders.map((f, fi) => (
                                    <Draggable key={`folder-${f.id}`} draggableId={`folder-${f.id}`} index={fi} isDragDisabled={!isDragEnabled}>
                                    {(prov, snap) => (
                                    <Box
                                        ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                                        key={f.id}
                                        onClick={() => isSearchResults ? revealFolderInColumns(f.id) : handleColumnSelect(colIdx, f.id)}
                                        sx={{
                                            px: 2, py: 1, cursor: isDragEnabled ? 'grab' : 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                                            bgcolor: selectedInCol === f.id ? selectedBg : 'transparent',
                                            opacity: snap.isDragging ? 0.7 : 1,
                                            ...itemHoverSx,
                                        }}
                                    >
                                        <FolderIcon sx={{ fontSize: 18, color: colors.blueAccent[400] }} />
                                        <Typography variant="body2" noWrap fontWeight={selectedInCol === f.id ? 600 : 400}>{f.name}</Typography>
                                        {f.children?.length > 0 && <NavigateNextIcon sx={{ fontSize: 16, ml: 'auto', color: colors.grey[500] }} />}
                                    </Box>
                                    )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                </Box>
                                )}
                                </Droppable>
                                {col.tests.length > 0 && col.folders.length > 0 && <Divider sx={{ my: 0.5 }} />}
                                <Droppable droppableId={`col-${colIdx}-tests`} isDropDisabled={!isDragEnabled}>
                                {(provided) => (
                                <Box ref={provided.innerRef} {...provided.droppableProps}>
                                {col.tests.map((t, ti) => (
                                    <Draggable key={`test-${t.id}`} draggableId={`test-${t.id}`} index={ti} isDragDisabled={!isDragEnabled}>
                                    {(prov, snap) => (
                                    <Box
                                        ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                                        key={t.id}
                                        onClick={() => navigate(`/admin/tests-v2/${t.id}/edit`)}
                                        sx={{ px: 2, py: 1, cursor: isDragEnabled ? 'grab' : 'pointer', display: 'flex', alignItems: 'center', gap: 1, opacity: snap.isDragging ? 0.7 : 1, ...itemHoverSx }}
                                    >
                                        <DescriptionIcon sx={{ fontSize: 18, color: colors.greenAccent[400] }} />
                                        <Typography variant="body2" noWrap>{t.title}</Typography>
                                    </Box>
                                    )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                </Box>
                                )}
                                </Droppable>
                                {col.folders.length === 0 && col.tests.length === 0 && col.id !== null && (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>Empty</Typography>
                                )}
                            </Box>
                        );
                    })}
                    {columns.length <= 1 && (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.primary[600] }}>
                            <Typography color="text.secondary">Select a folder</Typography>
                        </Box>
                    )}
                </Box>
                </DragDropContext>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={contextMenu?.anchorEl}
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
            >
                {contextMenu?.type === 'category' && [
                    <MenuItem key="rename" onClick={() => { setRenameFolderId(contextMenu.item.id); setRenameFolderName(contextMenu.item.name); setContextMenu(null); }}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Rename
                    </MenuItem>,
                    <MenuItem key="move" onClick={() => { openMoveDialog(contextMenu.item.id, 'folder'); setContextMenu(null); }}>
                        <DriveFileMoveIcon fontSize="small" sx={{ mr: 1 }} /> Move to Folder
                    </MenuItem>,
                    <MenuItem key="delete" onClick={() => { handleDeleteCategory(contextMenu.item.id); setContextMenu(null); }}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} /> Delete Folder
                    </MenuItem>,
                ]}
                {contextMenu?.type === 'test' && [
                    <MenuItem key="assign" onClick={() => { openQuickAssign(contextMenu.item.id, contextMenu.item.title); setContextMenu(null); }}>
                        <SendIcon fontSize="small" sx={{ mr: 1 }} /> Assign to Course
                    </MenuItem>,
                    <MenuItem key="edit" onClick={() => { navigate(`/admin/tests-v2/${contextMenu.item.id}/edit`); setContextMenu(null); }}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                    </MenuItem>,
                    <MenuItem key="duplicate" onClick={() => { navigate(`/admin/tests-v2/add?duplicate=${contextMenu.item.id}`); setContextMenu(null); }}>
                        <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
                    </MenuItem>,
                    <MenuItem key="move" onClick={() => { openMoveDialog(contextMenu.item.id, 'test'); setContextMenu(null); }}>
                        <DriveFileMoveIcon fontSize="small" sx={{ mr: 1 }} /> Move to Folder
                    </MenuItem>,
                    <MenuItem key="delete" onClick={() => { handleDeleteTest(contextMenu.item.id); setContextMenu(null); }}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} /> Delete
                    </MenuItem>,
                ]}
            </Menu>

            {/* Create Folder Dialog */}
            <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>New Folder</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Folder Name"
                        value={newFolderName}
                        onChange={(e) => { setNewFolderName(e.target.value); setCreateFolderError(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                        error={Boolean(createFolderError)}
                        helperText={createFolderError}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Autocomplete
                        fullWidth
                        options={allFoldersFlat}
                        value={allFoldersFlat.find(f => f.id === newFolderParentId) || allFoldersFlat[0] || null}
                        onChange={(_, val) => setNewFolderParentId(val?.id ?? null)}
                        getOptionLabel={(opt) => opt.path || opt.name}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderOption={(props, opt) => (
                            <li {...props} key={opt.id} style={opt.depth === 0 ? { borderTop: `1px solid ${colors.grey[700]}` } : {}}>
                                <Typography sx={{ whiteSpace: 'pre', fontWeight: opt.depth === 0 ? 700 : 400 }}>
                                    {opt.label}
                                    {opt.depth > 0 && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            {opt.path.slice(0, opt.path.lastIndexOf(' > '))}
                                        </Typography>
                                    )}
                                </Typography>
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Location" />}
                        ListboxProps={{ sx: { maxHeight: 250 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateFolder} variant="contained" color="secondary">Create</Button>
                </DialogActions>
            </Dialog>

            {/* Rename Folder Dialog */}
            <Dialog open={Boolean(renameFolderId)} onClose={() => setRenameFolderId(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Rename Folder</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Folder Name"
                        value={renameFolderName}
                        onChange={(e) => setRenameFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameFolderId(null)}>Cancel</Button>
                    <Button onClick={handleRenameFolder} variant="contained" color="secondary">Rename</Button>
                </DialogActions>
            </Dialog>

            {/* Move Dialog (tests + folders) */}
            <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Move {moveItemType === 'folder' ? 'Folder' : 'Test'}</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        fullWidth
                        options={allFoldersFlat}
                        value={allFoldersFlat.find(f => f.id === moveTargetFolderId) || allFoldersFlat[0] || null}
                        onChange={(_, val) => setMoveTargetFolderId(val?.id ?? null)}
                        getOptionLabel={(opt) => opt.path || opt.name}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderOption={(props, opt) => (
                            <li {...props} key={opt.id ?? 'root'} style={opt.depth === 0 ? { borderTop: `1px solid ${colors.grey[700]}` } : {}}>
                                <Typography sx={{ whiteSpace: 'pre', fontWeight: opt.depth === 0 ? 700 : 400 }}>
                                    {opt.label}
                                    {opt.depth > 0 && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            {opt.path.slice(0, opt.path.lastIndexOf(' > '))}
                                        </Typography>
                                    )}
                                </Typography>
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Destination Folder" />}
                        ListboxProps={{ sx: { maxHeight: 250 } }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMoveOpen(false)}>Cancel</Button>
                    <Button onClick={handleMove} variant="contained" color="secondary">Move</Button>
                </DialogActions>
            </Dialog>

            {/* Go to Course Dialog */}
            <Dialog open={goToCourseOpen} onClose={() => setGoToCourseOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Go to Course</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        fullWidth
                        loading={courseLoading}
                        options={filteredCourses}
                        value={selectedCourse}
                        onChange={(_, val) => setSelectedCourse(val)}
                        getOptionLabel={(opt) => opt.label}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderOption={(props, opt) => <li {...props} key={opt.id}>{opt.label}</li>}
                        renderInput={(params) => (
                            <TextField {...params} label="Search courses..." autoFocus
                                InputProps={{ ...params.InputProps, endAdornment: (<>{courseLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }}
                            />
                        )}
                        ListboxProps={{ sx: { maxHeight: 250 } }}
                        sx={{ mt: 1 }}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={currentSemesterOnly} onChange={(e) => setCurrentSemesterOnly(e.target.checked)} />}
                        label="Current semester only"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGoToCourseOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!selectedCourse}
                        onClick={() => {
                            setGoToCourseOpen(false);
                            window.open(`/classrooms/${selectedCourse.uuid}/tests`, '_blank');
                        }}
                    >
                        Go
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Quick Assign Dialog */}
            <Dialog open={quickAssignOpen} onClose={() => setQuickAssignOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign to Course</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <Autocomplete
                        fullWidth
                        loading={courseLoading}
                        options={filteredCourses}
                        value={quickAssignCourse}
                        onChange={(_, val) => {
                            setQuickAssignCourse(val);
                            setQuickAssignAllStudents([]);
                            setQuickAssignSelectedStudents([]);
                            if (val) {
                                setQuickAssignStudentsLoading(true);
                                v1(`/courses/${val.uuid}/students`).then(res => {
                                    const now = dayjs();
                                    const students = (res.data || []).filter(sc => {
                                        const start = dayjs(sc.start_date);
                                        const end = dayjs(sc.end_date);
                                        if (start.isValid() && now.isBefore(start)) return false;
                                        if (end.isValid() && now.isAfter(end)) return false;
                                        return true;
                                    }).map(sc => sc.student).filter(Boolean);
                                    setQuickAssignAllStudents(students);
                                    setQuickAssignSelectedStudents(students);
                                }).catch(() => {}).finally(() => setQuickAssignStudentsLoading(false));
                            }
                        }}
                        getOptionLabel={(opt) => opt.label}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderOption={(props, opt) => <li {...props} key={opt.id}>{opt.label}</li>}
                        renderInput={(params) => (
                            <TextField {...params} label="Select course" autoFocus
                                InputProps={{ ...params.InputProps, endAdornment: (<>{courseLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }}
                            />
                        )}
                        ListboxProps={{ sx: { maxHeight: 250 } }}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={currentSemesterOnly} onChange={(e) => setCurrentSemesterOnly(e.target.checked)} />}
                        label="Current semester only"
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Title"
                            value={quickAssignTitle}
                            onChange={(e) => setQuickAssignTitle(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Author"
                            defaultValue={localStorage.getItem('userName')}
                            inputProps={{ readOnly: true }}
                            sx={{ width: '40%' }}
                        />
                    </Box>
                    <TextField
                        type="datetime-local"
                        label="Due Date"
                        value={quickAssignDueDate}
                        onChange={(e) => setQuickAssignDueDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <TextField
                        label="Instructions (optional)"
                        value={quickAssignInstructions}
                        onChange={(e) => setQuickAssignInstructions(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                    />
                    {/* Student picker — all pre-selected, remove to exclude */}
                    {quickAssignCourse && (
                        <Autocomplete
                            multiple
                            limitTags={4}
                            loading={quickAssignStudentsLoading}
                            options={quickAssignAllStudents}
                            value={quickAssignSelectedStudents}
                            onChange={(_, val) => setQuickAssignSelectedStudents(val)}
                            getOptionLabel={(s) => `${s.first_name} ${s.last_name}`}
                            getOptionKey={(s) => s.id}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Students (remove to exclude)"
                                    InputProps={{ ...params.InputProps, endAdornment: (<>{quickAssignStudentsLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                                />
                            )}
                        />
                    )}

                    {quickAssignError && (
                        <Typography variant="body2" color="error">{quickAssignError}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQuickAssignOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleQuickAssign}
                        disabled={quickAssignLoading || !quickAssignCourse || !quickAssignDueDate}
                        startIcon={quickAssignLoading ? <CircularProgress size={16} /> : <SendIcon />}
                    >
                        {quickAssignLoading ? 'Assigning...' : 'Assign'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog ref={confirmRef} />
        </Box>
    );
};

export default TestLibrary;
